
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Check, X, Trash2, Package, User, MapPin, Receipt } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { PurchaseReceiptDialog } from "@/components/purchases/PurchaseReceiptDialog";

interface PurchaseRequestDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: any;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  onStatusUpdate: (requestId: string, status: string) => void;
  onDelete: (requestId: string) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const PurchaseRequestDetailsDialog = ({
  open,
  onOpenChange,
  request,
  getStatusColor,
  getStatusText,
  onStatusUpdate,
  onDelete,
  isUpdating,
  isDeleting,
}: PurchaseRequestDetailsDialogProps) => {
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);

  if (!request) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Détails de la demande d'achat
            </DialogTitle>
            <DialogDescription>
              Informations complètes sur la demande d'achat
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Statut */}
            <div className="flex justify-between items-center">
              <span className="font-medium">Statut:</span>
              <Badge className={getStatusColor(request.status)}>
                {getStatusText(request.status)}
              </Badge>
            </div>

            {/* Informations du produit */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Produit
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Nom:</span>
                  <span>{request.product?.title || 'Produit supprimé'}</span>
                </div>
                {request.product?.description && (
                  <div className="flex justify-between">
                    <span className="font-medium">Description:</span>
                    <span className="text-right max-w-[60%]">{request.product.description}</span>
                  </div>
                )}
                {request.product?.price && (
                  <div className="flex justify-between">
                    <span className="font-medium">Prix unitaire:</span>
                    <span>{request.product.price}€</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium">Quantité demandée:</span>
                  <span>{request.quantity}</span>
                </div>
                {request.product?.price && (
                  <div className="flex justify-between font-medium text-lg">
                    <span>Total:</span>
                    <span className="text-green-600">{(request.product.price * request.quantity).toFixed(2)}€</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Informations de l'acheteur */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Acheteur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Nom:</span>
                  <span>{request.buyer?.full_name || 'Utilisateur inconnu'}</span>
                </div>
                {request.buyer?.email && (
                  <div className="flex justify-between">
                    <span className="font-medium">Email:</span>
                    <span>{request.buyer.email}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium">Date de demande:</span>
                  <span>{format(new Date(request.created_at), "PPPp", { locale: fr })}</span>
                </div>
              </CardContent>
            </Card>

            {/* Adresse de livraison/récupération */}
            {request.pickup_address && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Adresse de livraison/récupération
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{request.pickup_address}</p>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {request.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Notes de l'acheteur</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{request.notes}</p>
                </CardContent>
              </Card>
            )}

            <Separator />

            {/* Actions */}
            <div className="flex gap-2 flex-wrap justify-end">
              {request.status === 'pending' && (
                <>
                  <Button
                    variant="default"
                    onClick={() => {
                      onStatusUpdate(request.id, 'approved');
                      onOpenChange(false);
                    }}
                    disabled={isUpdating}
                    className="flex items-center gap-2"
                  >
                    <Check className="h-4 w-4" />
                    Approuver
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      onStatusUpdate(request.id, 'rejected');
                      onOpenChange(false);
                    }}
                    disabled={isUpdating}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Refuser
                  </Button>
                </>
              )}

              {request.status === 'approved' && (
                <Button
                  variant="outline"
                  onClick={() => setShowReceiptDialog(true)}
                  className="flex items-center gap-2"
                >
                  <Receipt className="h-4 w-4" />
                  Voir le reçu
                </Button>
              )}

              <Button
                variant="ghost"
                onClick={() => {
                  onDelete(request.id);
                  onOpenChange(false);
                }}
                disabled={isDeleting}
                className="text-destructive hover:text-destructive flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PurchaseReceiptDialog
        open={showReceiptDialog}
        onOpenChange={setShowReceiptDialog}
        purchaseRequestId={request.id}
      />
    </>
  );
};
